import * as B from '@blueprintjs/core';
import {ErrorBoundary} from '@sentry/react';
import {PluginOptionsByType} from 'chart.js';
import {AnnotationOptions} from 'chartjs-plugin-annotation';
import * as geojson from 'geojson';
import * as I from 'immutable';
import React from 'react';

import AnalyzePolygonChart, {
  LoadingContainer as ChartLoadingContainer,
} from 'app/components/AnalyzePolygonChart/AnalyzePolygonChart';
import {DataRange, Graph, GraphRange} from 'app/components/AnalyzePolygonChart/types';
import {Y_SCALE_ID} from 'app/components/AnalyzePolygonChart/utils';
import {
  GraphMode,
  defaultOverlayThresholdForDataRange,
} from 'app/components/AnalyzePolygonPopup/AnalyzePolygonPopup';
import {
  useCalculatorData,
  useRasterTimeSeriesCalculator,
} from 'app/components/AnalyzePolygonPopup/AnalyzePolygonPopupWithState';
import {
  DEFAULT_GRAPH_MODE,
  getLayerSettings,
} from 'app/components/AnalyzePolygonPopup/layerSettings';
import {
  calculateDataPoints,
  createLoadingProps,
  fetchTileData,
} from 'app/components/AnalyzePolygonPopup/utils';
import {
  AlertThresholdDirection,
  ApiFeature,
  ApiFeatureData,
} from 'app/modules/Remote/Feature/types';
import {ApiFeatureCollection} from 'app/modules/Remote/FeatureCollection';
import {ApiOrganizationAreaUnit, getAreaUnit} from 'app/modules/Remote/Organization';
import {useAuth, useUserInfo} from 'app/providers/AuthProvider';
import {useFeatureData} from 'app/providers/FeatureDataProvider';
import {getDataRange as getLayerDataRange} from 'app/providers/MapPolygonStateProvider';
import {TileData, getFeatureDataHasThumbnailUrls} from 'app/stores/RasterCalculationStore';
import colorUtils from 'app/utils/colorUtils';
import * as featureUtils from 'app/utils/featureUtils';
import {filterFeatureData} from 'app/utils/featureUtils';
import {UTMArea} from 'app/utils/geoJsonUtils';
import {DEFAULT_GRADIENT_STOPS} from 'app/utils/gradientStops';
import {StatusMaybe} from 'app/utils/hookUtils';
import {DataLayerInfo, ImageLayerInfo} from 'app/utils/layers';
import * as layerUtils from 'app/utils/layerUtils';

import cs from './styles.module.scss';

const EMPTY_FEATURE_DATA: I.ImmutableListOf<ApiFeatureData> = I.List([]);

type ChartAnnotations = PluginOptionsByType<'line'>['annotation']['annotations'];

const clampToDataRange = (value: number, layerDataRange: DataRange) => {
  const [dataMin, dataMax] = layerDataRange;
  return Math.min(Math.max(value, dataMin), dataMax);
};

/**
 * If there is data in the last year, use the last full calendar year.
 * If there is no data in the last year, use the last 3 data points.
 */
function getDataRangeForPreview(
  featureData: I.ImmutableListOf<ApiFeatureData>,
  layerKey: string,
  graphMode: GraphMode
): {graphRange: GraphRange; startDate: Date} {
  const now = new Date();
  const previousYear = now.getUTCFullYear() - 1;
  const lastYearStart = new Date(Date.UTC(previousYear, 0, 1, 0, 0, 0, 0));
  const lastYearEnd = new Date(Date.UTC(previousYear, 11, 31, 23, 59, 59, 999));

  const filteredData = filterFeatureData(featureData, layerKey);

  const dates = filteredData.map((datum) => new Date(datum!.get('date'))).toArray();

  if (graphMode === 'areaByCategory' && dates.length) {
    const sortedDates = [...dates].sort((a, b) => a.getTime() - b.getTime());
    const years = Array.from(new Set(sortedDates.map((date) => date.getUTCFullYear()))).sort(
      (a, b) => a - b
    );

    return {
      graphRange: {
        type: 'some-years',
        options: years.map((year) => ({
          label: year.toString(),
          value: year,
          isSelected: true,
        })),
      },
      startDate: sortedDates[0],
    };
  }

  // Check if we have data covering the last full calendar year
  const hasDataInLastYear = dates.some((date) => date >= lastYearStart && date <= lastYearEnd);

  if (hasDataInLastYear) {
    return {
      graphRange: {type: 'custom', range: [lastYearStart, lastYearEnd]},
      startDate: lastYearStart,
    };
  }

  // No data in last year, use last 3 data points
  const lastThreeDates = dates.slice(-3);
  if (lastThreeDates.length === 0) {
    return {
      graphRange: {type: 'custom', range: [lastYearStart, lastYearEnd]},
      startDate: lastYearStart,
    };
  }

  const earliestDate = lastThreeDates[0];
  const latestDate = lastThreeDates[lastThreeDates.length - 1];

  return {
    graphRange: {type: 'custom', range: [earliestDate, latestDate]},
    startDate: earliestDate,
  };
}

/**
 * Calculate how many times the alert would have been triggered based on the current configuration.
 * This mimics the backend logic in lens_alerts.py, which uses state tracking:
 * - Checks all overlapping windows (doesn't skip ahead)
 * - Tracks whether an alert is currently "active" - we've already alerted about the current series of scenes
 * - Only counts new alert activations (when threshold is met and alert was inactive)
 * - Deactivates alert when threshold is no longer met
 */
function calculateAlertCount(
  graph: Graph | null,
  thresholdValue: number | null,
  thresholdDirection: AlertThresholdDirection | undefined,
  numConsecutiveScenes: number
): number {
  if (
    !graph ||
    thresholdValue === null ||
    !thresholdDirection ||
    graph.graphMode === 'areaByCategory'
  ) {
    return 0;
  }

  const dataPoints = graph.dataPoints;
  if (dataPoints.length < numConsecutiveScenes) {
    return 0;
  }

  let alertCount = 0;
  let isAlertActive = false;

  // Create sliding windows of length numConsecutiveScenes (matching backend)
  for (let i = 0; i <= dataPoints.length - numConsecutiveScenes; i++) {
    const window = dataPoints.slice(i, i + numConsecutiveScenes);

    // Check if all scenes in the window meet the threshold
    const allMeetThreshold = window.every((point) => {
      const [_x, y] = point;
      if (thresholdDirection === 'above') {
        return y > thresholdValue;
      } else {
        return y < thresholdValue;
      }
    });

    if (allMeetThreshold && !isAlertActive) {
      alertCount++;
      isAlertActive = true;
    } else if (!allMeetThreshold && isAlertActive) {
      isAlertActive = false;
    }
    // If allMeetThreshold && isAlertActive: alert stays active, no new count
    // If !allMeetThreshold && !isAlertActive: alert stays inactive, no action
  }

  return alertCount;
}

interface ChartContentProps {
  selectedFeatureGeometry: geojson.Polygon | geojson.MultiPolygon | null;
  isFeatureDataLoading: boolean;
  chartHasError: boolean;
  chartGraph: Graph | null;
  thresholdLineValue: number | null;
  thresholdDirection: AlertThresholdDirection | undefined;
  thresholdBand: {min: number | null; max: number | null} | null;
  bandAnnotations: ChartAnnotations | undefined;
  numConsecutiveScenes: number;
  graphLayerKey: string;
  propertyAreaInM2: number;
  areaUnit: ApiOrganizationAreaUnit;
  loadingProps: ReturnType<typeof createLoadingProps>;
  showAlertCount: boolean;
}

const ChartErrorState: React.FC = () => (
  <ChartLoadingContainer className={cs.chartLoading}>
    <B.Icon icon="warning-sign" intent={B.Intent.WARNING} />
    <p>We couldn't load a preview for this dataset.</p>
  </ChartLoadingContainer>
);

const ChartContent: React.FC<ChartContentProps> = ({
  selectedFeatureGeometry,
  isFeatureDataLoading,
  chartHasError,
  chartGraph,
  thresholdLineValue,
  thresholdDirection,
  thresholdBand,
  bandAnnotations,
  numConsecutiveScenes,
  graphLayerKey,
  propertyAreaInM2,
  areaUnit,
  loadingProps,
  showAlertCount,
}) => {
  const alertCount = React.useMemo(
    () =>
      calculateAlertCount(chartGraph, thresholdLineValue, thresholdDirection, numConsecutiveScenes),
    [chartGraph, thresholdLineValue, thresholdDirection, numConsecutiveScenes]
  );

  if (!selectedFeatureGeometry) {
    return (
      <ChartLoadingContainer className={cs.chartLoading}>
        <span>No property boundary available for preview.</span>
      </ChartLoadingContainer>
    );
  }

  if (isFeatureDataLoading) {
    return (
      <ChartLoadingContainer className={cs.chartLoading}>
        <B.Spinner size={20} />
        <p>Loading property data…</p>
      </ChartLoadingContainer>
    );
  }

  if (chartHasError) {
    return <ChartErrorState />;
  }

  if (chartGraph) {
    return (
      <>
        <AnalyzePolygonChart
          graph={chartGraph}
          graphMetadata={null}
          imageRefs={[{layerKey: graphLayerKey, cursor: null}]}
          areaInM2={propertyAreaInM2}
          areaUnit={areaUnit}
          controls={[]}
          disablePanZoom
          hideLegend={chartGraph.graphMode !== 'areaByCategory'}
          isLegendInteractive={false}
          showTitle={false}
          graphStyle={{width: '100%'}}
          isExpanded={false}
          loading={loadingProps.loading}
          loadingProgress={loadingProps.loadingProgress}
          loadingStatus={loadingProps.loadingStatus}
          loadingIcon={loadingProps.loadingIcon}
          additionalAnnotations={
            // A composite draws one shaded band per condition sharing this layer; a single
            // condition draws its own dashed threshold line or range. Bands win when present.
            bandAnnotations
              ? bandAnnotations
              : thresholdLineValue !== null
                ? {
                    'alert-threshold-line': {
                      type: 'line',
                      scaleID: Y_SCALE_ID,
                      value: thresholdLineValue,
                      borderColor: colorUtils.darkRed,
                      borderDash: [6, 4],
                      borderWidth: 2,
                    },
                    'alert-threshold-line-shade': {
                      type: 'box',
                      scaleID: Y_SCALE_ID,
                      yMin: thresholdDirection === 'below' ? thresholdLineValue : undefined,
                      yMax: thresholdDirection === 'above' ? thresholdLineValue : undefined,
                      backgroundColor: `${colorUtils.lightOrange}20`, // 20 = ~12% opacity in hex
                      borderWidth: 0,
                    },
                  }
                : thresholdBand
                  ? {
                      ...(thresholdBand.min !== null
                        ? {
                            'alert-threshold-band-min': {
                              type: 'line',
                              scaleID: Y_SCALE_ID,
                              value: thresholdBand.min,
                              borderColor: colorUtils.darkRed,
                              borderDash: [6, 4],
                              borderWidth: 2,
                            },
                          }
                        : {}),
                      ...(thresholdBand.max !== null
                        ? {
                            'alert-threshold-band-max': {
                              type: 'line',
                              scaleID: Y_SCALE_ID,
                              value: thresholdBand.max,
                              borderColor: colorUtils.darkRed,
                              borderDash: [6, 4],
                              borderWidth: 2,
                            },
                          }
                        : {}),
                      'alert-threshold-band-shade': {
                        type: 'box',
                        scaleID: Y_SCALE_ID,
                        yMin: thresholdBand.min ?? undefined,
                        yMax: thresholdBand.max ?? undefined,
                        backgroundColor: `${colorUtils.lightOrange}20`,
                        borderWidth: 0,
                      },
                    }
                  : undefined
          }
        />
        {showAlertCount && thresholdLineValue !== null && thresholdDirection && (
          <B.Callout
            icon={<B.Icon icon="notifications" size={12} />}
            intent={B.Intent.PRIMARY}
            compact
            style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}
          >
            <span style={{fontSize: '1.2rem'}}>
              {`This alert would've been sent approximately ${alertCount} ${alertCount === 1 ? 'time' : 'times'} last year, not accounting for spatial rules or monitoring season.`}
            </span>
          </B.Callout>
        )}
      </>
    );
  }

  if (loadingProps.loading) {
    return (
      <ChartLoadingContainer className={cs.chartLoading}>
        <B.Spinner size={20} value={loadingProps.loadingProgress ?? undefined} />
        <p>Generating preview…</p>
      </ChartLoadingContainer>
    );
  }

  return (
    <ChartLoadingContainer className={cs.chartLoading}>
      <span>No data available for the selected timeframe.</span>
    </ChartLoadingContainer>
  );
};

export interface AlertDatasetPreviewProps {
  featureCollection: I.ImmutableOf<ApiFeatureCollection>;
  feature: ApiFeature;
  layer: DataLayerInfo | ImageLayerInfo;
  thresholdValue?: number | null;
  thresholdDirection?: AlertThresholdDirection | undefined;
  /** For a two-sided range condition, where a single threshold line/direction can't represent the rule. */
  thresholdBand?: {min: number | null; max: number | null} | null;
  /**
   * One band per condition sharing this layer, for a composite chart that merges conditions from
   * multiple groups onto a single time series instead of drawing one chart each. Additive to
   * thresholdValue/thresholdBand, which stay the single-condition path.
   */
  bands?: {min: number | null; max: number | null; color: string; label: string}[];
  percentOfProperty?: number;
  numConsecutiveScenes?: number;
  /** Caption identifying what the chart is of, shown above the chart. */
  title?: string;
  /** The "would've been sent approximately N times" callout is only meaningful for a single condition's own count. */
  showAlertCount?: boolean;
}

export const AlertPreviewChart: React.FC<AlertDatasetPreviewProps> = ({
  featureCollection,
  feature,
  layer,
  thresholdValue,
  thresholdDirection,
  thresholdBand,
  bands,
  numConsecutiveScenes = 1,
  title,
  showAlertCount = true,
}) => {
  const auth = useAuth();
  const firebaseToken = auth.status === 'logged in' ? auth.firebaseToken : '';

  const {getFeatureData} = useFeatureData();
  const featureDataStatusMaybe = getFeatureData(feature.id);
  const featureData = featureDataStatusMaybe.value || EMPTY_FEATURE_DATA;
  const isFeatureDataLoading = featureDataStatusMaybe.status === 'unknown';

  const [organization] = useUserInfo();
  const areaUnit = getAreaUnit(organization);

  /** Same logic as AA list */
  const supportedLayerKeys = React.useMemo(() => {
    const paidImagery = featureUtils.getPaidImagery(I.fromJS(feature));
    const paidScenes = paidImagery?.get('scenes');
    if (!featureData) {
      return [];
    }
    return featureUtils.getAnalysisLayerKeysForFeature(
      featureCollection,
      featureData,
      paidScenes,
      I.fromJS(feature)
    );
  }, [featureCollection, featureData, feature]);

  // TODO: We could possibly change this to support overlay intersections
  const selectedFeatureGeometry = React.useMemo<
    geojson.Polygon | geojson.MultiPolygon | null
  >(() => {
    if (!feature?.geometry) return null;
    return feature.geometry as geojson.Polygon | geojson.MultiPolygon;
  }, [feature]);

  /** Needed for tooltips  */
  const propertyAreaInM2 = React.useMemo(
    () => (selectedFeatureGeometry ? UTMArea(selectedFeatureGeometry) : 0),
    [selectedFeatureGeometry]
  );

  const graphLayerKey = layer.key;
  const chartGraphMode = React.useMemo(
    () => getLayerSettings(graphLayerKey).defaultGraphMode ?? DEFAULT_GRAPH_MODE,
    [graphLayerKey]
  );
  const {graphRange: chartGraphRange, startDate: chartStartDate} = React.useMemo(
    () => getDataRangeForPreview(featureData, graphLayerKey, chartGraphMode),
    [featureData, graphLayerKey, chartGraphMode]
  );
  const rawLayerKey = layerUtils.getRawLayerKey(graphLayerKey);
  const hasThumbnailUrls = featureData
    ? getFeatureDataHasThumbnailUrls(featureData, rawLayerKey)
    : false;

  const tileData: StatusMaybe<TileData> = React.useMemo(() => {
    if (!selectedFeatureGeometry || !featureData) {
      return {status: 'unknown'};
    }
    return fetchTileData(graphLayerKey, selectedFeatureGeometry, featureData);
  }, [featureData, graphLayerKey, selectedFeatureGeometry]);

  const layerDataRange = React.useMemo(() => getLayerDataRange(layer), [layer]);

  const [rasterCalculator, chartLoadingProgress, chartLoadingWarning, chartLoadingError] =
    useRasterTimeSeriesCalculator(
      firebaseToken,
      featureData,
      graphLayerKey,
      true,
      chartGraphRange,
      tileData,
      hasThumbnailUrls,
      chartStartDate
    );

  /** Mostly setup for threshold line below */
  const chartThresholdLineValue = React.useMemo<number | null>(() => {
    if (
      layer.type !== 'data' ||
      typeof thresholdValue !== 'number' ||
      Number.isNaN(thresholdValue)
    ) {
      return null;
    }

    const [dataMin, dataMax] = layerDataRange;
    return Math.min(Math.max(thresholdValue, dataMin), dataMax);
  }, [layer, layerDataRange, thresholdValue]);

  const chartThresholdBand = React.useMemo<{min: number | null; max: number | null} | null>(() => {
    if (layer.type !== 'data' || !thresholdBand) {
      return null;
    }
    if (thresholdBand.min === null && thresholdBand.max === null) {
      return null;
    }

    return {
      min: thresholdBand.min !== null ? clampToDataRange(thresholdBand.min, layerDataRange) : null,
      max: thresholdBand.max !== null ? clampToDataRange(thresholdBand.max, layerDataRange) : null,
    };
  }, [layer, layerDataRange, thresholdBand]);

  const chartBands = React.useMemo(() => {
    if (layer.type !== 'data' || !bands?.length) {
      return [];
    }
    return bands
      .filter((band) => band.min !== null || band.max !== null)
      .map((band) => ({
        ...band,
        min: band.min !== null ? clampToDataRange(band.min, layerDataRange) : null,
        max: band.max !== null ? clampToDataRange(band.max, layerDataRange) : null,
      }));
  }, [layer, layerDataRange, bands]);

  // One dashed line per bound plus a translucent box per band, keyed by index so multiple bands
  // (and multiple lines within one band) never collide on the annotation plugin's key map.
  const chartBandAnnotations = React.useMemo<ChartAnnotations | undefined>(() => {
    if (chartBands.length === 0) {
      return undefined;
    }
    const annotations: Record<string, AnnotationOptions> = {};
    chartBands.forEach((band, index) => {
      if (band.min !== null) {
        annotations[`condition-band-${index}-min`] = {
          type: 'line',
          scaleID: Y_SCALE_ID,
          value: band.min,
          borderColor: band.color,
          borderDash: [6, 4],
          borderWidth: 2,
        };
      }
      if (band.max !== null) {
        annotations[`condition-band-${index}-max`] = {
          type: 'line',
          scaleID: Y_SCALE_ID,
          value: band.max,
          borderColor: band.color,
          borderDash: [6, 4],
          borderWidth: 2,
        };
      }
      annotations[`condition-band-${index}-shade`] = {
        type: 'box',
        scaleID: Y_SCALE_ID,
        yMin: band.min ?? undefined,
        yMax: band.max ?? undefined,
        backgroundColor: `${band.color}20`, // 20 = ~12% opacity in hex
        borderWidth: 0,
      };
    });
    return annotations;
  }, [chartBands]);

  const chartOverlayThreshold = React.useMemo<[number, number]>(() => {
    return defaultOverlayThresholdForDataRange(layerDataRange);
  }, [layerDataRange]);

  const chartCalculatorData = useCalculatorData(
    chartOverlayThreshold,
    selectedFeatureGeometry,
    rasterCalculator,
    chartLoadingProgress
  );

  const chartGraph = React.useMemo<Graph | null>(() => {
    if (!selectedFeatureGeometry || chartCalculatorData.status !== 'loaded') {
      return null;
    }

    const dataPoints = calculateDataPoints(
      chartCalculatorData,
      chartGraphMode,
      graphLayerKey,
      propertyAreaInM2
    );

    const layerDataLabels = 'dataLabels' in layer ? layer.dataLabels : undefined;
    const layerDataUnit = 'dataUnit' in layer ? layer.dataUnit : undefined;
    const layerGradientStops =
      'gradientStops' in layer && layer.gradientStops
        ? layer.gradientStops
        : DEFAULT_GRADIENT_STOPS;

    return {
      dataPoints,
      dataThreshold: chartOverlayThreshold,
      layerDisplay: layer.display,
      layerDataRange,
      layerDataLabels,
      layerDataUnit,
      layerGradientStops,
      layerKey: graphLayerKey,
      graphMode: chartGraphMode,
      graphModePreset: null,
      graphRange: chartGraphRange,
      graphDataRange: null,
      graphDataRangeMode: 'none',
      graphTimeRange: null,
      dataColumns: null,
    };
  }, [
    chartCalculatorData,
    chartGraphMode,
    chartGraphRange,
    chartOverlayThreshold,
    graphLayerKey,
    layer,
    layerDataRange,
    propertyAreaInM2,
    selectedFeatureGeometry,
  ]);

  const loadingProps = createLoadingProps(chartCalculatorData);
  const chartHasError =
    tileData.status === 'error' || chartCalculatorData.status === 'error' || !!chartLoadingError;
  const showWarning = !!selectedFeatureGeometry && featureData && !chartHasError;

  if (!supportedLayerKeys.includes(graphLayerKey)) {
    return null;
  }

  return (
    <B.FormGroup
      label={title ?? 'Dataset preview'}
      subLabel={
        // A composite renders several of these at once and states the timeframe once, above them
        // all, rather than repeating it under every chart.
        !bands && chartGraph?.graphMode !== 'areaByCategory'
          ? 'Chart shows data from the past calendar year (Jan 1 – Dec 31) for this property, or the last 3 data points if no data is available for that period. '
          : ''
      }
    >
      <div className={cs.chartPreview}>
        <ErrorBoundary fallback={<ChartErrorState />}>
          <ChartContent
            selectedFeatureGeometry={selectedFeatureGeometry}
            isFeatureDataLoading={isFeatureDataLoading}
            chartHasError={chartHasError}
            chartGraph={chartGraph}
            thresholdLineValue={chartThresholdLineValue}
            thresholdDirection={thresholdDirection}
            thresholdBand={chartThresholdBand}
            bandAnnotations={chartBandAnnotations}
            numConsecutiveScenes={numConsecutiveScenes}
            graphLayerKey={graphLayerKey}
            propertyAreaInM2={propertyAreaInM2}
            areaUnit={areaUnit}
            loadingProps={loadingProps}
            showAlertCount={showAlertCount}
          />
        </ErrorBoundary>
        {showWarning && chartLoadingWarning && (
          <B.Callout icon="warning-sign" intent={B.Intent.WARNING} className={cs.chartCallout}>
            {chartLoadingWarning}
          </B.Callout>
        )}
      </div>
    </B.FormGroup>
  );
};
